/**
 * FAQ scrubber.
 *
 * The external Valentine Digital / OTTO SEO loader has an auto-FAQ
 * generator that occasionally emits nonsense entries on routes that
 * have no real, page-specific questions (notably /gallery and
 * /reviews). Examples seen in production:
 *
 *   "What is gallery?"
 *   "Why does gallery fail?"
 *   "What is reviews?"
 *   "Why does reviews fail?"
 *
 * These show up both as visible accordions injected into the DOM and
 * as `FAQPage` JSON-LD entries in `document.head`. They're invalid
 * FAQ schema and embarrassing copy.
 *
 * This scrubber:
 *   1. Removes matching FAQ entries from any `FAQPage` JSON-LD block
 *      in `document.head`. If a FAQPage ends up empty, the whole
 *      `<script>` is dropped.
 *   2. Removes visible DOM nodes whose text content starts with one
 *      of the garbage question patterns (and their immediate answer
 *      siblings inside the same accordion/section).
 *   3. Runs idempotently and via MutationObserver so re-injection by
 *      the external loader is caught.
 *
 * The patterns are conservative on purpose — only auto-generated
 * meta-questions about the page name itself ("What is <page>?",
 * "Why does <page> fail?", "How does <page> work?") match. Hand-
 * written FAQs in our own `FaqSection` are untouched.
 */

const GARBAGE_PATTERNS: RegExp[] = [
  /^\s*what\s+is\s+(gallery|reviews?|about|contact|home|blog|projects?|services?)\??\s*$/i,
  /^\s*why\s+does\s+(gallery|reviews?|about|contact|home|blog|projects?|services?)\s+fail\??\s*$/i,
  /^\s*how\s+does\s+(gallery|reviews?|about|contact|home|blog|projects?|services?)\s+work\??\s*$/i,
  /^\s*when\s+to\s+use\s+(gallery|reviews?|about|contact|home|blog|projects?|services?)\??\s*$/i,
];

export function isGarbageQuestion(text: string | null | undefined): boolean {
  if (!text) return false;
  return GARBAGE_PATTERNS.some((re) => re.test(text));
}

/** Scrub garbage entries from a single FAQPage JSON-LD payload. Returns null if the payload should be dropped entirely. */
export function scrubFaqJsonLd(payload: unknown): unknown | null {
  if (!payload || typeof payload !== "object") return payload;
  const obj = payload as Record<string, unknown>;
  const type = obj["@type"];
  const isFaq =
    type === "FAQPage" ||
    (Array.isArray(type) && type.includes("FAQPage"));
  if (!isFaq) return payload;

  const main = obj.mainEntity;
  if (!Array.isArray(main)) return payload;

  const clean = main.filter((entry) => {
    const name = (entry as { name?: unknown })?.name;
    return !isGarbageQuestion(typeof name === "string" ? name : null);
  });

  if (clean.length === 0) return null;
  if (clean.length === main.length) return payload;
  return { ...obj, mainEntity: clean };
}

function scrubJsonLdScripts(root: ParentNode = document.head): number {
  const scripts = root.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  );
  let changed = 0;
  for (const el of Array.from(scripts)) {
    const raw = el.textContent?.trim();
    if (!raw) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      continue;
    }
    const next = Array.isArray(parsed)
      ? parsed.map((p) => scrubFaqJsonLd(p)).filter((p) => p !== null)
      : scrubFaqJsonLd(parsed);

    if (next === null || (Array.isArray(next) && next.length === 0)) {
      el.remove();
      changed++;
      continue;
    }
    const serialized = JSON.stringify(next);
    if (serialized !== raw && serialized !== JSON.stringify(parsed)) {
      el.textContent = serialized;
      changed++;
    }
  }
  return changed;
}

/** Heuristic: an FAQ container is anything that exposes one of our
 * garbage questions as a question heading. We climb to a reasonable
 * container (closest section / details / div with role="region" or
 * data-faq) and remove the whole thing. Safe because hand-written
 * FAQs never produce these question strings. */
function scrubDomGarbage(root: ParentNode = document.body): number {
  if (!root) return 0;
  let removed = 0;
  // Walk visible text-bearing question candidates.
  const candidates = root.querySelectorAll<HTMLElement>(
    "h1, h2, h3, h4, h5, summary, button, span, p, dt",
  );
  for (const el of Array.from(candidates)) {
    const text = (el.textContent || "").trim();
    if (!isGarbageQuestion(text)) continue;
    // Find the smallest semantic container that owns this question.
    const container =
      el.closest(
        '[data-faq], [data-faq-item], details, article, section, li, div[role="region"], div[role="group"]',
      ) || el.parentElement || el;
    container.remove();
    removed++;
  }
  return removed;
}

let started = false;
let observer: MutationObserver | null = null;
let scheduled = false;

function sweep() {
  try {
    scrubJsonLdScripts(document.head);
    if (document.body) scrubDomGarbage(document.body);
  } catch {
    /* never let the scrubber throw into product code */
  }
}

function scheduleSweep() {
  if (scheduled) return;
  scheduled = true;
  // Defer to microtask so we batch bursts of injection from the
  // external loader.
  queueMicrotask(() => {
    scheduled = false;
    sweep();
  });
}

export function startFaqScrubber(): void {
  if (started || typeof document === "undefined") return;
  started = true;

  // Initial pass (head may already be populated by the edge-rewritten
  // HTML before hydration).
  sweep();

  observer = new MutationObserver(() => scheduleSweep());
  // Watch both head (for JSON-LD) and body (for rendered FAQ blocks).
  observer.observe(document.head, { childList: true, subtree: true });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        if (document.body) observer!.observe(document.body, { childList: true, subtree: true });
        sweep();
      },
      { once: true },
    );
  }

  // Re-sweep on route changes — React Router doesn't fire a native
  // event, but popstate covers back/forward and a custom event we
  // already emit elsewhere ("route-status-change") covers app-driven
  // navs.
  window.addEventListener("popstate", () => scheduleSweep());
  window.addEventListener("route-status-change", () => scheduleSweep());
}

// Exposed for tests.
export const __internal = { scrubJsonLdScripts, scrubDomGarbage, sweep };